import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Heart, User, ShoppingBag, Menu, X, Coins, ChevronDown, Recycle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { SUBCATEGORIES } from "../lib/catalog";
import { LogoMark } from "./Logo";

const LINKS = [
  { to: "/brands", label: "Brands" },
  { to: "/pre-loved", label: "Second-Life" },
  { to: "/offers", label: "Offers" },
  { to: "/about", label: "About Jigel" },
];

export const Wordmark = ({ light }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
    <LogoMark color={light ? "#F9F8F6" : "#111111"} size={28} />
    <span className={`font-serif text-2xl md:text-3xl tracking-[0.22em] font-medium ${light ? "text-ivory" : "text-ink"}`}>JIGEL</span>
  </Link>
);

function ShopMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button data-testid="nav-shop" className={`flex items-center gap-1 text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 hover:text-wine ${open ? "text-wine" : "text-ink/70"}`}>
        Shop <ChevronDown size={12} strokeWidth={1.5} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 z-50" data-testid="shop-mega-menu">
          <div className="bg-ivory border border-border shadow-2xl shadow-ink/10 w-[640px] grid grid-cols-3 gap-8 p-8">
            {[["men", "Men"], ["women", "Women"], ["kids", "Kids"]].map(([key, label]) => (
              <div key={key}>
                <Link to={`/${key}`} data-testid={`mega-${key}`} onClick={() => setOpen(false)}
                  className="font-serif text-xl hover:text-wine transition-colors">{label}</Link>
                <div className="mt-4 flex flex-col gap-2">
                  {SUBCATEGORIES[key].slice(0, 8).map((c) => (
                    <Link key={c} to={`/${key}?category=${encodeURIComponent(c)}`} onClick={() => setOpen(false)}
                      data-testid={`mega-${key}-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      className="text-sm text-ink/60 hover:text-wine transition-colors">{c}</Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SearchBar() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [suggests, setSuggests] = useState({ products: [], brands: [] });
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setSuggests({ products: [], brands: [] }); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.get("/search/suggest", { params: { q } }).then((r) => setSuggests(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
    setQ("");
  };

  if (!open) {
    return (
      <button data-testid="search-btn" onClick={() => setOpen(true)} aria-label="Search" className="flex items-center gap-2">
        <Search size={18} strokeWidth={1.5} className="hover:text-wine transition-colors" />
        <span className="hidden xl:inline text-[11px] tracking-[0.2em] uppercase text-ink/50">Search</span>
      </button>
    );
  }

  return (
    <div className="relative" data-testid="search-bar">
      <form onSubmit={submit} className="flex items-center gap-2 border-b border-ink pb-1.5 w-56 md:w-80">
        <Search size={15} strokeWidth={1.5} className="text-muted-foreground shrink-0" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
          onBlur={() => setTimeout(() => setSuggests({ products: [], brands: [] }), 250)}
          placeholder="Levi's jeans, Nike shoes, dresses…"
          className="w-full bg-transparent outline-none text-sm placeholder:text-muted-foreground/60" />
        <button type="button" onClick={() => { setOpen(false); setQ(""); }} aria-label="Close search" data-testid="search-close"><X size={14} strokeWidth={1.5} /></button>
      </form>
      {(suggests.products.length > 0 || suggests.brands.length > 0) && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-ivory border border-border shadow-2xl shadow-ink/10 z-50" data-testid="search-suggestions">
          {suggests.brands.map((b) => (
            <Link key={b.slug} to={`/brands/${b.slug}`} onClick={() => { setOpen(false); setQ(""); }} data-testid={`suggest-brand-${b.slug}`}
              className="flex items-center justify-between px-4 py-2.5 hover:bg-sand/60 transition-colors">
              <span className="font-serif text-lg">{b.name}</span>
              <span className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground">Brand</span>
            </Link>
          ))}
          {suggests.products.map((p) => (
            <Link key={p.product_id} to={`/product/${p.product_id}`} onClick={() => { setOpen(false); setQ(""); }} data-testid={`suggest-product-${p.product_id}`}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-sand/60 transition-colors">
              <img src={p.images?.[0]} alt="" className="w-9 h-11 object-cover bg-sand" />
              <div className="min-w-0">
                <p className="text-sm truncate">{p.name}</p>
                <p className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground">{p.brand}</p>
              </div>
            </Link>
          ))}
          <button onClick={submit} data-testid="search-view-all" className="w-full text-left px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-wine border-t border-border hover:bg-sand/60">
            View all results for “{q}”
          </button>
        </div>
      )}
    </div>
  );
}

export default function Nav() {
  const { user } = useAuth();
  const { count } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let alive = true;
    const fetchUnread = () => api.get("/notifications/mine").then((r) => { if (alive) setUnread(r.data.unread); }).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-ivory/90 border-b border-border/60">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-[72px] gap-6">
          <button data-testid="mobile-menu-btn" className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            {menuOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
          <Wordmark />
          <nav className="hidden lg:flex items-center gap-7" data-testid="main-nav">
            <ShopMenu />
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) => `text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 hover:text-wine ${isActive ? "text-wine" : "text-ink/70"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4 md:gap-5">
            <Link to="/sell" data-testid="nav-sell"
              className="hidden md:flex items-center gap-1.5 bg-wine text-ivory text-[10px] tracking-[0.18em] uppercase px-4 py-2 hover:bg-ink transition-colors duration-300">
              <Recycle size={13} strokeWidth={1.5} /> Sell Your Clothes
            </Link>
            <Link to="/cloth-cash" data-testid="nav-cloth-cash"
              className="hidden md:flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase border border-gold/60 text-gold px-3 py-2 hover:bg-gold hover:text-ink transition-colors duration-300">
              <Coins size={13} strokeWidth={1.5} /> Cloth Cash
            </Link>
            <SearchBar />
            <Link to="/account?tab=wishlist" data-testid="wishlist-btn" aria-label="Wishlist">
              <Heart size={18} strokeWidth={1.5} className="hover:text-wine transition-colors" />
            </Link>
            <Link to={user ? (user.role === "admin" ? "/admin" : "/account") : "/auth"} data-testid="account-btn" className="relative" aria-label="Account">
              <User size={18} strokeWidth={1.5} className="hover:text-wine transition-colors" />
              {unread > 0 && (
                <span data-testid="unread-badge" className="absolute -top-2 -right-2 bg-gold text-ink text-[9px] min-w-4 h-4 px-0.5 rounded-full flex items-center justify-center font-mono">{unread}</span>
              )}
            </Link>
            <Link to="/cart" data-testid="cart-btn" className="relative" aria-label="Cart">
              <ShoppingBag size={18} strokeWidth={1.5} className="hover:text-wine transition-colors" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-wine text-ivory text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">{count}</span>
              )}
            </Link>
          </div>
        </div>
      </div>
      {menuOpen && (
        <nav className="lg:hidden border-t border-border/60 bg-ivory max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-5 py-6 flex flex-col gap-1">
            <Link to="/sell" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-sell"
              className="py-3 font-serif text-2xl text-wine">Sell Your Clothes</Link>
            {[["men", "Men"], ["women", "Women"], ["kids", "Kids"]].map(([key, label]) => (
              <div key={key}>
                <button onClick={() => setExpanded(expanded === key ? null : key)} data-testid={`mobile-nav-${key}`}
                  className="w-full flex items-center justify-between py-3 font-serif text-2xl">
                  {label} <ChevronDown size={16} strokeWidth={1.5} className={`transition-transform ${expanded === key ? "rotate-180" : ""}`} />
                </button>
                {expanded === key && (
                  <div className="pb-3 pl-4 grid grid-cols-2 gap-x-4">
                    <Link to={`/${key}`} onClick={() => setMenuOpen(false)} className="py-1.5 text-sm text-wine">Shop All {label}</Link>
                    {SUBCATEGORIES[key].map((c) => (
                      <Link key={c} to={`/${key}?category=${encodeURIComponent(c)}`} onClick={() => setMenuOpen(false)}
                        className="py-1.5 text-sm text-ink/60">{c}</Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {[["/brands", "Brands"], ["/pre-loved", "Second-Life"], ["/offers", "Offers"], ["/cloth-cash", "Cloth Cash"], ["/about", "About Jigel"]].map(([to, label]) => (
              <NavLink key={to} to={to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="py-3 font-serif text-2xl">{label}</NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
